import {Phone, Calendar, Shield, Sparkles, Check, MapPin, Award, Palette, Droplets, Sun} from 'lucide-react'
import { useNavigate } from "react-router-dom"
import { useEffect } from "react"
import CanonicalTag from '@/components/CanonicalTag'
import EmbeddedQuoteForm from '@/components/EmbeddedQuoteForm'

const ColoredPpfSanDiegoPage = () => {
  const navigate = useNavigate()

  useEffect(() => {
    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What is colored PPF?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Colored PPF (color paint protection film) is a pigmented or finished urethane film that changes your vehicle's color while delivering the same self-healing, rock-chip, and UV protection as clear PPF. It combines the look of a color change wrap with the durability of paint protection film."
          }
        },
        {
          "@type": "Question",
          "name": "How is colored PPF different from a vinyl wrap?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Colored PPF is thicker, self-healing, and warranty-backed for paint protection, while vinyl wrap is primarily cosmetic. Colored PPF resists rock chips, scratches, and UV damage, lasts up to 10 years, and protects the factory paint underneath, unlike standard vinyl."
          }
        },
        {
          "@type": "Question",
          "name": "How much does colored PPF cost in San Diego?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Colored PPF starts at $5,999 for standard vehicles. Larger vehicles, complex bodywork, specialty finishes (color-shift, metallic, custom-matched), and additional prep or paint correction may cost more. Exact pricing is determined after a free consultation and vehicle inspection."
          }
        },
        {
          "@type": "Question",
          "name": "What finishes are available for colored PPF?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Colored PPF is available in gloss, satin, matte, and stealth finishes, plus a wide range of popular colors including stealth black, satin gray, gloss white, and custom shades."
          }
        },
        {
          "@type": "Question",
          "name": "Is colored PPF good for Teslas?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. Colored PPF is one of the most popular upgrades for Tesla Model Y, Model 3, Model S, and Model X owners in San Diego. It transforms Tesla's soft factory paint with a stealth, satin, or gloss color change while adding self-healing rock-chip and UV protection that preserves resale value."
          }
        },
        {
          "@type": "Question",
          "name": "What is the best finish for colored PPF?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "The best colored PPF finish depends on your style. Stealth and satin finishes are the most requested for a modern, aggressive look with easy upkeep, gloss delivers a deep factory-like shine, and matte gives a bold flat appearance. All finishes offer the same self-healing paint protection."
          }
        },
        {
          "@type": "Question",
          "name": "Is colored PPF worth the extra cost?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "For most owners, yes. Colored PPF costs more than vinyl wrap but delivers true paint protection, self-healing, longer warranties up to 10 years, and better resale value by shielding the factory paint. You pay once for both a color change and full protection instead of two separate services."
          }
        }
      ]
    })
    document.head.appendChild(script)

    // Service + LocalBusiness + Breadcrumb structured data
    const bizScript = document.createElement('script')
    bizScript.type = 'application/ld+json'
    bizScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Colored Paint Protection Film (Color Change PPF)",
      "name": "Colored PPF San Diego",
      "description": "Color change paint protection film installation in San Diego - gloss, satin, matte, and stealth finishes with self-healing, warranty-backed protection for Tesla, Corvette, Jeep, trucks, and luxury vehicles.",
      "areaServed": ["San Diego", "Vista", "Oceanside", "Carlsbad", "San Marcos", "Encinitas", "North County San Diego"],
      "provider": {
        "@type": "LocalBusiness",
        "name": "Mr Wrap - AutoWrapSanDiego",
        "image": "https://autowrapsandiego.com/lumi.ing/logo.png",
        "telephone": "+1-858-282-8119",
        "priceRange": "$$$",
        "url": "https://autowrapsandiego.com/colored-ppf-san-diego",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "2244 South Santa Fe Avenue, Suite A7",
          "addressLocality": "Vista",
          "addressRegion": "CA",
          "postalCode": "92084",
          "addressCountry": "US"
        },
        "areaServed": "San Diego County"
      }
    })
    document.head.appendChild(bizScript)

    const breadcrumbScript = document.createElement('script')
    breadcrumbScript.type = 'application/ld+json'
    breadcrumbScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://autowrapsandiego.com/" },
        { "@type": "ListItem", "position": 2, "name": "Paint Protection Film San Diego", "item": "https://autowrapsandiego.com/paint-protection-film-san-diego" },
        { "@type": "ListItem", "position": 3, "name": "Colored PPF San Diego", "item": "https://autowrapsandiego.com/colored-ppf-san-diego" }
      ]
    })
    document.head.appendChild(breadcrumbScript)

    document.title = "Colored PPF San Diego | Color Change Paint Protection Film"
    const metaDescription = document.querySelector('meta[name="description"]')
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Colored PPF in San Diego - color change paint protection film with self-healing, warranty-backed protection. Stealth, satin, matte & gloss finishes. Serving Vista, Oceanside, Carlsbad, San Marcos, Encinitas. Call 858-282-8119.')
    }

    return () => {
      document.head.removeChild(script)
      document.head.removeChild(bizScript)
      document.head.removeChild(breadcrumbScript)
    }
  }, [])

  return (
    <>
      <CanonicalTag url="https://autowrapsandiego.com/colored-ppf-san-diego" />
      <div className="pt-20 min-h-screen bg-black">
      {/* Hero */}
      <section className="bg-gradient-to-br from-black via-gray-900 to-black text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-6">Colored Paint Protection Film San Diego - Color Change PPF</h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-4">
              Get the bold new look of a color change wrap with the unmatched durability of paint protection film. Colored PPF in San Diego transforms your vehicle's appearance with stealth, satin, matte, and gloss finishes while delivering self-healing, rock-chip, and UV protection that vinyl simply can't match.
            </p>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Serving Vista, Oceanside, Carlsbad, San Marcos, Encinitas, and all of San Diego County. Our installers apply premium warranty-backed colored paint protection film using bulk or precision-pattern methods selected by vehicle and panel for a clean, factory-quality result.
            </p>
            <div className="flex gap-4 justify-center flex-wrap">
              <a href="tel:858-282-8119" className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                <Phone size={18} />
                858-282-8119
              </a>
              <button onClick={() => { navigate("/contact"); setTimeout(() => window.scrollTo(0, 0), 100); }} className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                <Calendar size={18} />
                Get Free Colored PPF Quote
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Breadcrumb Nav */}
      <nav aria-label="Breadcrumb" className="bg-black border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <ol className="flex items-center gap-2 text-sm text-gray-400 flex-wrap">
            <li><a href="/" className="hover:text-cyan-400 transition">Home</a></li>
            <li className="text-gray-600">›</li>
            <li><a href="/paint-protection-film-san-diego" className="hover:text-cyan-400 transition">Paint Protection Film San Diego</a></li>
            <li className="text-gray-600">›</li>
            <li className="text-cyan-400 font-medium" aria-current="page">Colored PPF San Diego</li>
          </ol>
        </div>
      </nav>

      {/* Trust Strip */}
      <section className="bg-gray-950 border-b border-cyan-500/20 py-5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex items-center gap-3">
              <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-2 flex-shrink-0">
                <Award className="text-cyan-400" size={22} />
              </div>
              <div>
                <p className="text-white font-semibold text-sm leading-tight">13 Years of Experience</p>
                <p className="text-gray-400 text-xs">Professional PPF & wraps</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-2 flex-shrink-0">
                <Shield className="text-cyan-400" size={22} />
              </div>
              <div>
                <p className="text-white font-semibold text-sm leading-tight">Warranty-Backed Films</p>
                <p className="text-gray-400 text-xs">Premium materials up to 10 yr</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-2 flex-shrink-0">
                <MapPin className="text-cyan-400" size={22} />
              </div>
              <div>
                <p className="text-white font-semibold text-sm leading-tight">Vista / North County SD</p>
                <p className="text-gray-400 text-xs">Serving all San Diego County</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-2 flex-shrink-0">
                <Sparkles className="text-cyan-400" size={22} />
              </div>
              <div>
                <p className="text-white font-semibold text-sm leading-tight">Tesla · Corvette · Jeep</p>
                <p className="text-gray-400 text-xs">Trucks, luxury & exotic builds</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Summary / Key Takeaways */}
      <section className="py-16 bg-gray-900 border-y border-cyan-500/20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-3 text-white text-center">Color Change PPF San Diego - Quick Summary</h2>
          <p className="text-gray-400 text-center mb-10 max-w-3xl mx-auto">Colored Paint Protection Film gives San Diego drivers a complete color change plus genuine paint protection in a single installation. Here are the biggest benefits at a glance:</p>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3 bg-black p-5 rounded-xl border border-cyan-500/20">
              <Check className="text-cyan-400 mt-1 flex-shrink-0" size={22} />
              <p className="text-gray-300"><strong className="text-white">Color change + protection in one:</strong> Transform your color with stealth, satin, matte, or gloss while shielding the factory paint underneath.</p>
            </div>
            <div className="flex items-start gap-3 bg-black p-5 rounded-xl border border-cyan-500/20">
              <Check className="text-cyan-400 mt-1 flex-shrink-0" size={22} />
              <p className="text-gray-300"><strong className="text-white">Self-healing finish:</strong> Light scratches and swirl marks disappear with heat - something vinyl wrap can never do.</p>
            </div>
            <div className="flex items-start gap-3 bg-black p-5 rounded-xl border border-cyan-500/20">
              <Check className="text-cyan-400 mt-1 flex-shrink-0" size={22} />
              <p className="text-gray-300"><strong className="text-white">Rock-chip & UV defense:</strong> Thick urethane absorbs road debris and blocks San Diego's intense sun to prevent fading.</p>
            </div>
            <div className="flex items-start gap-3 bg-black p-5 rounded-xl border border-cyan-500/20">
              <Check className="text-cyan-400 mt-1 flex-shrink-0" size={22} />
              <p className="text-gray-300"><strong className="text-white">Warranty-backed & long-lasting:</strong> Premium colored PPF lasts up to 10 years and protects resale value far better than vinyl.</p>
            </div>
          </div>
        </div>
      </section>

      {/* What is Colored PPF */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">What Is Colored Paint Protection Film?</h2>
          <div className="max-w-4xl mx-auto space-y-6 text-gray-300 text-lg">
            <p>
              Colored Paint Protection Film—also called color change PPF or colored PPF—is a pigmented, finished urethane film that transforms your vehicle's color while simultaneously protecting the paint underneath. Unlike traditional clear PPF that's invisible, colored paint protection film comes in a full spectrum of shades and finishes, giving you a complete color change San Diego drivers love, with the rock-solid protection of premium film.
            </p>
            <p>
              The result is the best of both worlds: the dramatic transformation of a color change combined with the self-healing, impact-resistant durability of <a href="/paint-protection-film-san-diego" className="text-cyan-400 hover:underline">paint protection film</a>. Searching for "colored PPF near me" in San Diego? Our Vista facility specializes in stealth PPF, satin PPF, and gloss PPF installations on Tesla, Porsche, <a href="/bmw-ppf-san-diego" className="text-cyan-400 hover:underline">BMW</a>, Mercedes, and every other make and model.
            </p>
            <p>
              Where a standard <a href="/color-change-wrap-san-diego" className="text-cyan-400 hover:underline">color change wrap</a> uses cosmetic vinyl, colored PPF uses a thicker, warranty-backed protective film engineered to absorb rock chips and resist scratches. It's the premium choice for owners who want a striking new color and serious paint protection in one installation.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits over vinyl wrap */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Colored Paint Protection Film Benefits Over Vinyl Wrap</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Shield className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Real Paint Protection</h3>
              <p className="text-gray-300">Thick urethane construction absorbs rock chips and road debris that would shred standard vinyl, protecting your factory paint while changing your color.</p>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Sparkles className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Self-Healing Finish</h3>
              <p className="text-gray-300">Light scratches and swirl marks disappear with heat from the sun—something vinyl wrap can never do. Your color stays flawless for years.</p>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Sun className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Superior UV Resistance</h3>
              <p className="text-gray-300">Advanced UV inhibitors prevent fading and yellowing under San Diego's intense sun, keeping your color vibrant far longer than vinyl.</p>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Award className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Warranty-Backed</h3>
              <p className="text-gray-300">Colored Paint Protection Film is backed by manufacturer warranties up to 10 years against yellowing, cracking, and delamination—far beyond typical vinyl coverage.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Self-Healing & Paint Protection Technology */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Self-Healing Technology & Paint Protection Benefits</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Droplets className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-white">Self-Healing Topcoat</h3>
              <p className="text-gray-300 mb-4">Colored Paint Protection Film features an elastomeric self-healing topcoat that repairs minor scratches, scuffs, and swirl marks when exposed to heat from the sun or warm water. Your colored finish stays smooth and showroom-fresh without polishing or correction.</p>
              <p className="text-gray-400 text-sm">Heals light marring automatically</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Shield className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-white">Rock Chip & Impact Defense</h3>
              <p className="text-gray-300 mb-4">The thick urethane base absorbs impact energy from rock chips, road debris, and parking lot dings on freeways like I-5, I-15, and Highway 78—protecting both your new color and the factory paint beneath it.</p>
              <p className="text-gray-400 text-sm">Protects paint and resale value</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Sun className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-white">UV & Oxidation Protection</h3>
              <p className="text-gray-300 mb-4">Built-in UV inhibitors shield your paint from San Diego's coastal sun and salt air, preventing fading and oxidation while keeping your colored finish rich and consistent for years.</p>
              <p className="text-gray-400 text-sm">Color stays vibrant long-term</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Droplets className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-white">Stain & Contaminant Resistance</h3>
              <p className="text-gray-300 mb-4">The hydrophobic topcoat repels water, dirt, bug splatter, bird droppings, and tree sap—making your colored PPF easy to wash and maintain in San Diego's coastal environment.</p>
              <p className="text-gray-400 text-sm">Easier cleaning and upkeep</p>
            </div>
          </div>
        </div>
      </section>

      {/* Finish Options */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Gloss, Satin, Matte & Stealth Finish Options</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Sparkles className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Gloss PPF</h3>
              <p className="text-gray-300">Deep, reflective gloss colored PPF mimics premium factory paint with a wet, mirror-like shine—perfect for owners who want a vibrant, classic finish with full protection.</p>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Palette className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Satin PPF</h3>
              <p className="text-gray-300">Satin PPF blends matte and gloss for a refined, low-sheen look with a smooth texture. Sophisticated and modern, with easier upkeep than full matte.</p>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Palette className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Matte PPF</h3>
              <p className="text-gray-300">Flat, non-reflective matte colored PPF delivers an aggressive, custom appearance while protecting your paint—popular in matte black, gray, and military green.</p>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Shield className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Stealth PPF</h3>
              <p className="text-gray-300">Stealth PPF transforms glossy factory paint into a striking satin-matte stealth finish while adding full self-healing protection—our most requested colored PPF look.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Mid-page CTA */}
      <section className="py-14 bg-gradient-to-r from-cyan-600 to-cyan-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4 text-white">Not Sure Which Finish Is Right for You?</h2>
          <p className="text-lg text-white/90 mb-6">Our Vista colored PPF specialists will help you choose the perfect gloss, satin, matte, or stealth finish for your vehicle. Free, no-pressure consultation.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:858-282-8119" className="bg-white hover:bg-gray-100 text-gray-900 font-bold py-3 px-10 rounded-lg transition inline-flex items-center justify-center gap-2">
              <Phone size={20} />
              Call 858-282-8119
            </a>
            <button onClick={() => { navigate("/contact"); setTimeout(() => window.scrollTo(0, 0), 100); }} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 px-10 rounded-lg transition inline-flex items-center justify-center gap-2">
              <Calendar size={20} />
              Get Free Colored PPF Quote
            </button>
          </div>
        </div>
      </section>

      {/* Popular Colors */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Popular Colored Paint Protection Film Colors</h2>
          <div className="max-w-4xl mx-auto space-y-6 text-gray-300 text-lg">
            <p>
              Colored Paint Protection Film is available in an extensive palette to match any style. The most requested finishes among San Diego drivers include:
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mt-10 max-w-5xl mx-auto">
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold text-white mb-2">Stealth Black</h3>
              <p className="text-gray-400 text-sm">Satin-matte stealth black over gloss paint—aggressive and timeless.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold text-white mb-2">Satin Gray (Nardo-Style)</h3>
              <p className="text-gray-400 text-sm">Sophisticated satin gray inspired by performance European finishes.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold text-white mb-2">Gloss White / Frozen White</h3>
              <p className="text-gray-400 text-sm">Crisp gloss or matte pearl white for a clean, premium look.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold text-white mb-2">Matte Military Green</h3>
              <p className="text-gray-400 text-sm">Bold matte olive green for trucks, SUVs, and off-road builds.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold text-white mb-2">Gloss Deep Blue</h3>
              <p className="text-gray-400 text-sm">Rich, reflective blues that pop in San Diego sunlight.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold text-white mb-2">Custom & Special Shades</h3>
              <p className="text-gray-400 text-sm">Color-shift, metallic, and custom-matched options on request.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Colored Paint Protection Film Pricing in San Diego</h2>
          <div className="max-w-2xl mx-auto bg-black p-10 rounded-xl border-2 border-cyan-500 text-center">
            <div className="bg-cyan-500 text-black text-xs font-bold px-3 py-1 rounded-full inline-block mb-4">COLORED PPF</div>
            <h3 className="text-2xl font-bold mb-4 text-white">Full Vehicle Color Change + Protection</h3>
            <div className="text-cyan-400 text-5xl font-bold mb-2">Starting at $5,999</div>
            <p className="text-gray-400 text-sm mb-8">Standard vehicles, gloss/satin finish</p>
            <ul className="space-y-2 text-gray-300 text-left max-w-md mx-auto">
              <li className="flex items-start gap-2">
                <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                <span>Complete color change + protection</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                <span>Choice of gloss, satin, matte, or stealth finish</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                <span>Bulk or precision-pattern installation</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                <span>Self-healing colored film</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                <span>Warranty-backed material, up to 10 years</span>
              </li>
            </ul>
          </div>
          <p className="text-center text-gray-400 mt-8 max-w-3xl mx-auto">
            Pricing above reflects a standard vehicle with a gloss or satin finish. Larger vehicles (full-size SUVs, trucks), complex bodywork (Jeep Wrangler, exotic supercars), specialty films (color-shift, metallic, custom-matched), and additional prep or paint correction may cost more. Exact pricing is always determined after a free consultation and vehicle inspection. Want partial coverage instead? Ask about custom quotes for hood, fenders, or full-front-only colored PPF. Compare with our <a href="/color-change-wrap-san-diego" className="text-cyan-400 hover:underline">color change wrap options</a>.
          </p>
        </div>
      </section>

      {/* Why customers choose colored PPF over traditional wraps */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Why Customers Choose Colored Paint Protection Film Over Traditional Wraps</h2>
          <div className="max-w-4xl mx-auto space-y-6 text-gray-300 text-lg">
            <p>
              Traditional vinyl wraps are purely cosmetic—they change your color but offer little protection against rock chips, scratches, or UV damage. Colored Paint Protection Film is fundamentally different: it's a true protective film that just happens to come in stunning colors and finishes. You get the look you want and the durability your paint deserves.
            </p>
            <p>
              San Diego drivers increasingly choose colored paint protection film over <a href="/vinyl-wraps" className="text-cyan-400 hover:underline">vinyl wraps</a> because it self-heals, resists daily wear, carries a longer warranty, and protects resale value by shielding the factory paint underneath. For enthusiasts who want both style and substance, colored PPF is the clear upgrade.
            </p>
            <p>
              Pair colored paint protection film with complementary services like <a href="/chrome-delete" className="text-cyan-400 hover:underline">chrome delete</a> and <a href="/window-tinting" className="text-cyan-400 hover:underline">window tinting</a> for a fully transformed, fully protected build. Whether you're searching "color change PPF San Diego" or "colored paint protection film near me," our team delivers a result that looks incredible and lasts for years.
            </p>
          </div>
        </div>
      </section>

      {/* Vehicle Applications */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-4 text-white text-center">Popular Vehicles for Colored Paint Protection Film in San Diego</h2>
          <p className="text-gray-400 text-center mb-12 max-w-3xl mx-auto">From Teslas to American muscle and exotic supercars, colored paint protection film is the top choice for San Diego drivers who want a custom color and lasting protection. Explore the vehicles we transform most:</p>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-3 text-white">Tesla Model Y Colored Paint Protection Film</h3>
              <p className="text-gray-300">The Tesla Model Y is San Diego's most-wrapped EV, and colored PPF is the smart upgrade. Tesla's notoriously soft factory paint chips easily on freeways like I-5 and I-15, so a stealth or satin colored PPF delivers a head-turning color change while shielding every high-impact panel. Explore our dedicated <a href="/tesla-wrap-san-diego" className="text-cyan-400 hover:underline">Tesla wrap options</a> and <a href="/tesla-ppf-san-diego" className="text-cyan-400 hover:underline">Tesla PPF protection</a>.</p>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-3 text-white">Tesla Model 3 Colored Paint Protection Film</h3>
              <p className="text-gray-300">Model 3 owners love colored PPF for a clean, modern transformation - satin gray, stealth black, and frozen white are top sellers. Because the Model 3's paint is thin and prone to rock chips, colored PPF protects resale value while giving the sedan a premium, custom appearance that vinyl simply can't match.</p>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-3 text-white">Tesla Model S Colored Paint Protection Film</h3>
              <p className="text-gray-300">The flagship Model S deserves flagship protection. Colored Paint Protection Film lets Model S owners refine their luxury sedan with deep gloss or sophisticated satin finishes while adding self-healing, warranty-backed defense against San Diego's sun and road debris - protecting both the look and the long-term value.</p>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-3 text-white">Tesla Model X Colored Paint Protection Film</h3>
              <p className="text-gray-300">With its large panels and falcon-wing doors, the Model X is a standout candidate for colored paint protection film. A full color change in stealth or matte gives this SUV an exclusive presence, while the thick film guards the expansive painted surfaces from chips, scratches, and UV fade.</p>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-3 text-white">Corvette Colored Paint Protection Film</h3>
              <p className="text-gray-300">The C8 Corvette and other performance Chevrolets are prime candidates for colored paint protection film. Owners choose stealth, satin, or gloss finishes to make their sports car unmistakable, while the impact-absorbing film protects the low-slung front end and rocker panels from rock chips during spirited San Diego canyon and coastal driving.</p>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-3 text-white">Jeep Wrangler Colored Paint Protection Film</h3>
              <p className="text-gray-300">Jeep Wrangler and off-road builds are perfect for matte and military-green colored paint protection film. Beyond the rugged custom look, the durable film shields paint from trail debris, brush, and sand on Anza-Borrego and coastal off-road trails - and unlike vinyl, it self-heals and protects the factory finish underneath.</p>
            </div>
          </div>
          <div className="bg-black p-8 rounded-xl border border-cyan-500/20 mt-8">
            <h3 className="text-2xl font-bold mb-3 text-white">Luxury & Exotic Vehicle Colored Paint Protection Film</h3>
            <p className="text-gray-300">Porsche, BMW, Mercedes-AMG, Audi, Lamborghini, Ferrari, and McLaren owners turn to colored paint protection film for a bespoke, head-turning finish backed by serious protection. High-value exotics demand careful, panel-specific installation — colored PPF lets you personalize a rare color or finish while preserving the priceless factory paint and resale value beneath. Considering a traditional color change instead? Compare with our <a href="/car-wrap-san-diego" className="text-cyan-400 hover:underline">car wrap services in San Diego</a>.</p>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-20 bg-black">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-4 text-white text-center">Colored Paint Protection Film vs Vinyl Wrap Comparison</h2>
          <p className="text-gray-400 text-center mb-12 max-w-3xl mx-auto">See how colored paint protection film compares to a traditional vinyl color change wrap across the factors that matter most:</p>
          <div className="overflow-x-auto rounded-xl border border-cyan-500/20">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-900">
                  <th className="p-4 text-white font-bold border-b border-cyan-500/20">Feature</th>
                  <th className="p-4 text-cyan-400 font-bold border-b border-cyan-500/20">Colored Paint Protection Film</th>
                  <th className="p-4 text-gray-300 font-bold border-b border-cyan-500/20">Vinyl Wrap</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="bg-black">
                  <td className="p-4 text-white font-semibold border-b border-gray-800">Thickness</td>
                  <td className="p-4 text-gray-300 border-b border-gray-800">Thick urethane (~8 mil)</td>
                  <td className="p-4 text-gray-400 border-b border-gray-800">Thin cosmetic vinyl (~2-3.5 mil)</td>
                </tr>
                <tr className="bg-gray-900/50">
                  <td className="p-4 text-white font-semibold border-b border-gray-800">Paint Protection</td>
                  <td className="p-4 text-gray-300 border-b border-gray-800">Full protective film</td>
                  <td className="p-4 text-gray-400 border-b border-gray-800">Minimal / cosmetic only</td>
                </tr>
                <tr className="bg-black">
                  <td className="p-4 text-white font-semibold border-b border-gray-800">Self-Healing</td>
                  <td className="p-4 text-gray-300 border-b border-gray-800">Yes - heals with heat</td>
                  <td className="p-4 text-gray-400 border-b border-gray-800">No</td>
                </tr>
                <tr className="bg-gray-900/50">
                  <td className="p-4 text-white font-semibold border-b border-gray-800">Scratch Resistance</td>
                  <td className="p-4 text-gray-300 border-b border-gray-800">High</td>
                  <td className="p-4 text-gray-400 border-b border-gray-800">Low</td>
                </tr>
                <tr className="bg-black">
                  <td className="p-4 text-white font-semibold border-b border-gray-800">Rock Chip Protection</td>
                  <td className="p-4 text-gray-300 border-b border-gray-800">Excellent</td>
                  <td className="p-4 text-gray-400 border-b border-gray-800">Very limited</td>
                </tr>
                <tr className="bg-gray-900/50">
                  <td className="p-4 text-white font-semibold border-b border-gray-800">Longevity</td>
                  <td className="p-4 text-gray-300 border-b border-gray-800">Up to 10 years</td>
                  <td className="p-4 text-gray-400 border-b border-gray-800">5-7 years</td>
                </tr>
                <tr className="bg-black">
                  <td className="p-4 text-white font-semibold border-b border-gray-800">Maintenance</td>
                  <td className="p-4 text-gray-300 border-b border-gray-800">Easy - hydrophobic topcoat</td>
                  <td className="p-4 text-gray-400 border-b border-gray-800">Moderate - more delicate</td>
                </tr>
                <tr className="bg-gray-900/50">
                  <td className="p-4 text-white font-semibold border-b border-gray-800">Finish Options</td>
                  <td className="p-4 text-gray-300 border-b border-gray-800">Gloss, satin, matte, stealth</td>
                  <td className="p-4 text-gray-400 border-b border-gray-800">Widest range of colors/finishes</td>
                </tr>
                <tr className="bg-black">
                  <td className="p-4 text-white font-semibold border-b border-gray-800">Cost</td>
                  <td className="p-4 text-gray-300 border-b border-gray-800">Higher (protection included)</td>
                  <td className="p-4 text-gray-400 border-b border-gray-800">Lower (cosmetic only)</td>
                </tr>
                <tr className="bg-gray-900/50">
                  <td className="p-4 text-white font-semibold">Best Use Case</td>
                  <td className="p-4 text-gray-300">Color change + lasting protection</td>
                  <td className="p-4 text-gray-400">Budget or short-term color change</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-center text-gray-400 mt-6">Prefer a pure cosmetic color change? Explore our <a href="/color-change-wrap-san-diego" className="text-cyan-400 hover:underline">color change wrap San Diego</a> and <a href="/vinyl-wraps" className="text-cyan-400 hover:underline">vinyl wrap services</a>.</p>
        </div>
      </section>

      {/* Why Choose Mr Wrap in Vista */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Why Choose Mr Wrap in Vista</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Award className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Professional Installers</h3>
              <p className="text-gray-300">Our experienced team specializes in colored PPF, with careful technique and manufacturer warranty documentation on the film selected for every install.</p>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <MapPin className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Convenient Vista Location</h3>
              <p className="text-gray-300">Centrally located in Vista at 2244 South Santa Fe Avenue, Suite A7—easy access from Oceanside, Carlsbad, San Marcos, Encinitas, and greater San Diego.</p>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Sparkles className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Flawless, Detail-Obsessed Work</h3>
              <p className="text-gray-300">Bulk or precision-pattern methods, full trim removal, and precision edge tucking deliver a seamless, factory-quality colored PPF finish.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Colored Paint Protection Film FAQs - San Diego</h2>
          <div className="space-y-6">
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">What is colored paint protection film?</h3>
              <p className="text-gray-300">Colored paint protection film (also called color change PPF) is a pigmented or specially-finished urethane film that changes your vehicle's color while delivering the same self-healing, rock-chip, and UV protection as clear PPF. It combines the look of a color change wrap with the durability of paint protection film.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How is colored paint protection film different from a vinyl wrap?</h3>
              <p className="text-gray-300">Colored paint protection film is thicker, self-healing, and warranty-backed for paint protection, while vinyl wrap is primarily cosmetic. Colored PPF resists rock chips, scratches, and UV damage, lasts up to 10 years, and protects the factory paint underneath—unlike standard vinyl, which offers minimal protection.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How much does colored paint protection film cost in San Diego?</h3>
              <p className="text-gray-300">Colored paint protection film starts at $5,999 for full-vehicle coverage on a standard vehicle. Larger vehicles, complex bodywork, specialty finishes like color-shift or metallic films, and additional paint prep may cost more. Partial coverage options are also available with custom pricing. Exact pricing is determined after a free consultation and vehicle inspection.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">What finishes are available for colored paint protection film?</h3>
              <p className="text-gray-300">Colored paint protection film is available in gloss, satin, matte, and stealth finishes. Popular colors include stealth black, satin gray, gloss white, frozen white, matte military green, and deep gloss blue, plus custom and color-shift options on request.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Does colored PPF protect against rock chips?</h3>
              <p className="text-gray-300">Yes. Unlike vinyl, colored PPF is built on a thick urethane base that absorbs impact from rock chips and road debris, protecting both the colored layer and the factory paint underneath—ideal for San Diego freeways like I-5, I-15, and Highway 78.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How long does colored PPF last?</h3>
              <p className="text-gray-300">Premium colored PPF lasts up to 10 years with proper care, backed by manufacturer warranties against yellowing, cracking, and delamination. Self-healing and UV protection keep the finish looking fresh far longer than vinyl wrap.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Is colored paint protection film good for Teslas?</h3>
              <p className="text-gray-300">Yes. Colored paint protection film is one of the most popular upgrades for Tesla Model Y, Model 3, Model S, and Model X owners in San Diego. Tesla's soft factory paint chips easily, so colored PPF delivers a stealth, satin, or gloss color change while adding self-healing rock-chip and UV protection that preserves resale value.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">What is the best finish for colored paint protection film?</h3>
              <p className="text-gray-300">The best colored PPF finish depends on your style. Stealth and satin are the most requested for a modern, aggressive look with easy upkeep, gloss delivers a deep factory-like shine, and matte gives a bold flat appearance. All finishes provide the same self-healing paint protection underneath.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Is colored paint protection film worth the extra cost?</h3>
              <p className="text-gray-300">For most owners, yes. Colored paint protection film costs more than vinyl wrap but delivers true paint protection, self-healing, warranties up to 10 years, and better resale value by shielding the factory paint. You pay once for both a color change and full protection instead of two separate services.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Embedded Quote Form */}
      <section className="py-16 bg-gray-950">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <EmbeddedQuoteForm
            defaultService="colored-ppf"
            heading="Get a Free Colored PPF Quote"
            subheading="Tell us about your vehicle and goals. Our Vista team will reach out with a personalized quote — no pressure, no obligation."
          />
        </div>
      </section>

      {/* Related Services */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8 text-white text-center">Related Services</h2>
          <p className="text-gray-300 text-center mb-8">
            Explore our paint protection and color transformation services:
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            <a href="/paint-protection-film-san-diego" className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Paint Protection Film San Diego</h3>
              <p className="text-gray-400 text-sm">Clear self-healing PPF for ultimate rock-chip and UV protection</p>
            </a>
            <a href="/color-change-wrap-san-diego" className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Color Change Wrap San Diego</h3>
              <p className="text-gray-400 text-sm">Full vinyl color change wraps in 500+ colors and finishes</p>
            </a>
            <a href="/vinyl-wraps" className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Vinyl Wraps</h3>
              <p className="text-gray-400 text-sm">Complete vinyl wrap services for all vehicle types</p>
            </a>
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section className="py-16 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8 text-white text-center flex items-center justify-center gap-3">
            <MapPin className="text-cyan-400" size={32} />
            Colored Paint Protection Film — Serving San Diego County
          </h2>
          <p className="text-gray-300 text-lg mb-6 text-center">
            Based in Vista, we provide professional colored PPF installation throughout San Diego County and North County.
          </p>
          <div className="grid md:grid-cols-3 gap-4 text-gray-300 text-sm">
            <div className="bg-gray-900 p-4 rounded-lg border border-cyan-500/20">
              <h3 className="font-bold text-white mb-2">North County Coastal</h3>
              <ul className="space-y-1">
                <li>• Oceanside</li>
                <li>• Carlsbad</li>
                <li>• Encinitas</li>
                <li>• Solana Beach</li>
                <li>• Del Mar</li>
              </ul>
            </div>
            <div className="bg-gray-900 p-4 rounded-lg border border-cyan-500/20">
              <h3 className="font-bold text-white mb-2">North County Inland</h3>
              <ul className="space-y-1">
                <li>• Vista (Shop Location)</li>
                <li>• San Marcos</li>
                <li>• Escondido</li>
                <li>• Rancho Santa Fe</li>
                <li>• Poway</li>
              </ul>
            </div>
            <div className="bg-gray-900 p-4 rounded-lg border border-cyan-500/20">
              <h3 className="font-bold text-white mb-2">Greater San Diego</h3>
              <ul className="space-y-1">
                <li>• San Diego</li>
                <li>• La Jolla</li>
                <li>• Mission Valley</li>
                <li>• Downtown SD</li>
                <li>• All SD County</li>
              </ul>
            </div>
          </div>
          <p className="text-gray-300 text-center mt-8">
            <strong className="text-white">Shop Address:</strong> 2244 South Santa Fe Avenue, Suite A7, Vista, CA 92084
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-cyan-500 to-cyan-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6 text-white">Ready for a Colored Paint Protection Film Transformation?</h2>
          <p className="text-xl text-white/90 mb-8">
            Schedule your free colored PPF consultation. Explore gloss, satin, matte, and stealth finishes with warranty-backed protection. Call 858-282-8119 today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => { navigate("/contact"); setTimeout(() => window.scrollTo(0, 0), 100); }} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
              <Calendar size={20} />
              Get Free Colored PPF Quote
            </button>
            <a href="tel:858-282-8119" className="bg-white hover:bg-gray-100 text-gray-900 font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
              <Phone size={20} />
              Call: 858-282-8119
            </a>
          </div>
        </div>
      </section>
      </div>
    </>
  )
}

export default ColoredPpfSanDiegoPage
